import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Info, Pencil } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ConnectivityStatus } from '@/components/ui/connectivity-status';
import { deviceStatusDisplay } from '@/lib/display-labels';
import { formatDateTime, formatRelativeTime } from '@/lib/utils';
import { DeviceType, type Device, type DeviceStatus, type Gateway, type MeterReading } from '@/types/api';
import {
  deviceTypeLabel,
  formatUnitOfMeasureSuffix,
  resolveEffectiveMultiplierLabel,
} from '@/features/devices/utils/salt-config';
import { hasFreshTransmission } from '@/features/devices/utils/has-fresh-transmission';

// May 18 batch — feedback consolidated the 3-block info card into TWO
// categories: "Device Info" (everything about what/where the meter is +
// usage) and "Connection" (network identity / freshness). Spacing was
// tightened (gap-y-3 within each grid, space-y-5 between sections, small
// uppercase section labels) because the previous layout felt empty.
//
// Status (account: Active/Provisioned/Inactive/Error) and Connectivity
// (network: Online/Joined/Intermittent/Offline) now carry an Info-icon
// tooltip to disambiguate them — a common point of confusion.
//
// Editable fields (Device Type, Multiplier, Status, Notes, IMR,
// Connected Meter Model/Serial) render as buttons that open the edit
// modal when canEdit is true. Active Alarms navigates to /alerts
// filtered by this device. The pencil affordance only fades in on
// hover so the default state stays calm.

const statusColors: Record<DeviceStatus, 'success' | 'warning' | 'error' | 'default'> = {
  Active: 'success',
  Provisioned: 'warning',
  Inactive: 'default',
  Error: 'error',
};

interface DeviceDetailsTabProps {
  device: Device;
  gateways?: Gateway[];
  // Used by the bottom usage facts to compute MTD delta when no
  // server-fetched boundary reading exists (mid-month installs).
  readings?: MeterReading[];
  // Server-fetched most-recent reading at-or-before the start of the
  // current month. Powers the exact "Total Usage this month" value.
  boundaryMonth?: MeterReading | null;
  // When set, editable fields render as clickable buttons that invoke
  // this callback. The parent owns the modal — passing undefined makes
  // every field read-only (read-only roles, embedded contexts).
  onEdit?: () => void;
}

function startOfCurrentMonth(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
}

function usageFromBoundary(
  latestTotalization: number | null,
  boundary: MeterReading | null | undefined,
): number | null {
  if (latestTotalization == null || !boundary) return null;
  const boundaryTotal = boundary.computedWaterTotalization ?? boundary.waterTotalization;
  if (boundaryTotal == null) return null;
  const delta = latestTotalization - boundaryTotal;
  return delta < 0 ? null : delta;
}

function usageSince(
  readings: MeterReading[],
  cutoff: Date,
  latestTotalization: number | null,
): { value: number; sinceTimestamp: string } | null {
  if (latestTotalization == null) return null;
  const cutoffMs = cutoff.getTime();
  const inWindow = readings.filter((r) => new Date(r.timestamp).getTime() >= cutoffMs);
  const earliest = inWindow.length > 0
    ? inWindow[inWindow.length - 1]
    : readings.find((r) => new Date(r.timestamp).getTime() < cutoffMs);
  if (!earliest) return null;
  const earliestTotal = earliest.computedWaterTotalization ?? earliest.waterTotalization;
  const delta = latestTotalization - earliestTotal;
  if (delta < 0) return null;
  return { value: delta, sinceTimestamp: earliest.timestamp };
}

export function DeviceDetailsTab({ device, gateways = [], readings = [], boundaryMonth, onEdit }: DeviceDetailsTabProps) {
  const navigate = useNavigate();
  const canEdit = !!onEdit;
  const gatewayEui = device.latestTransmission?.gatewayEui;
  const matchedGateway = gatewayEui
    ? gateways.find((g) => g.gatewayEui.toUpperCase() === gatewayEui.toUpperCase())
    : null;
  const isSalt = device.deviceType === DeviceType.SALTTransmitter;
  const tx = device.latestTransmission;
  const isFresh = hasFreshTransmission(device);
  const reading = isFresh && tx ? (tx.computedWaterTotalization ?? tx.waterTotalization) : null;
  const readingSuffix = formatUnitOfMeasureSuffix(tx?.computedUnitOfMeasure ?? tx?.unitOfMeasure);
  const customUomSuffix = device.customPulseUnitOfMeasure?.trim();
  const effectiveSuffix = customUomSuffix && customUomSuffix.length > 0 ? customUomSuffix : readingSuffix;
  const activeAlarmCount = device.activeAlerts?.length ?? 0;

  const exactMonth = isFresh ? usageFromBoundary(reading, boundaryMonth) : null;
  const monthFallback = isFresh ? usageSince(readings, startOfCurrentMonth(), reading) : null;
  const monthUsage = exactMonth != null
    ? { value: exactMonth, label: 'Since the 1st' }
    : monthFallback != null
      ? { value: monthFallback.value, label: `Since ${formatRelativeTime(monthFallback.sinceTimestamp)}` }
      : null;
  const rawTotalization = isFresh && tx?.waterTotalization != null ? tx.waterTotalization : null;
  const rawSuffix = formatUnitOfMeasureSuffix(tx?.unitOfMeasure);
  const mechanicalOffsetForDisplay = device.mechanicalOffset ?? device.baselineTotalization ?? null;
  const lastActive = isFresh && device.lastSeenAt
    ? formatRelativeTime(device.lastSeenAt)
    : device.lastSeenAt ? 'Awaiting…' : 'Never';

  return (
    <Card className="border border-gray-200 dark:border-gray-700">
      <div className="p-6 space-y-5">
        {/* ── Device Info ─────────────────────────────────────────────
           "What/where it is" + usage. Order is the client's prescribed
           sequence with Total Usage + Last Active promoted up next to
           Last Usage (per May 18 feedback). */}
        <section>
          <SectionLabel>Device Info</SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
            {device.propertyName && <Field label="Property" value={device.propertyName} />}
            <Field label="Building" value={device.buildingName ?? <Muted>Unassigned</Muted>} />
            <Field label="Unit" value={device.unitNumber ?? <Muted>—</Muted>} />
            {/* Device Type is only click-editable for SALT (where the
                modal exposes "Connected To" → meter-type dropdown). For
                a plain WaterMeter the modal has nothing to change here,
                so we leave it read-only to avoid opening a modal that
                looks like "Edit Notes" with no Device Type field. */}
            <Field
              label="Device Type"
              value={deviceTypeLabel(device)}
              onClick={canEdit && isSalt ? onEdit : undefined}
              editTitle="Edit connected meter type"
            />
            {isSalt && (
              <Field
                label="Multiplier"
                value={resolveEffectiveMultiplierLabel(device)}
                mono
                onClick={canEdit ? onEdit : undefined}
              />
            )}
            <Field
              label="Last Usage"
              value={
                reading != null ? (
                  <span className="tabular-nums">
                    {reading.toLocaleString()}
                    <span className="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400">
                      {effectiveSuffix}
                    </span>
                  </span>
                ) : (
                  <Muted>{isFresh ? 'No reading yet' : 'Awaiting first uplink'}</Muted>
                )
              }
            />
            <Field
              label="Total Usage this month"
              value={
                monthUsage != null ? (
                  <span className="tabular-nums">
                    {monthUsage.value.toLocaleString()}
                    <span className="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400">
                      {effectiveSuffix}
                    </span>
                    <span className="block text-xs font-normal text-gray-400 dark:text-gray-500 mt-0.5">
                      {monthUsage.label}
                    </span>
                  </span>
                ) : (
                  <Muted>{device.unitAssignedAt ? 'Awaiting first uplink' : 'No data yet'}</Muted>
                )
              }
            />
            <Field label="Last Active" value={lastActive} />
            <Field
              label="Status"
              hint="Account state — whether this device is Active, Provisioned, Inactive, or in Error. Separate from network connectivity."
              value={
                <Badge variant={statusColors[device.status]} size="sm" dot>
                  {deviceStatusDisplay(device.status, device.joinedAt)}
                </Badge>
              }
            />
            <Field label="Serial Number" value={device.serialNumber} mono />
            <Field
              label="Active Alarms"
              value={
                activeAlarmCount > 0 ? (
                  <Badge variant="error" size="sm">
                    <AlertTriangle className="w-3 h-3" />
                    {activeAlarmCount} active
                  </Badge>
                ) : (
                  <span className="text-sm text-gray-700 dark:text-gray-300">None</span>
                )
              }
              onClick={
                activeAlarmCount > 0
                  ? () => navigate(`/alerts?deviceId=${device.id}`)
                  : undefined
              }
              editTitle="View this device's alerts"
            />
            {isSalt && (
              <Field
                label="Raw reading"
                value={
                  rawTotalization != null ? (
                    <span className="tabular-nums">
                      {rawTotalization.toLocaleString()}
                      <span className="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400">
                        {rawSuffix}
                      </span>
                      {mechanicalOffsetForDisplay != null && (
                        <span className="block text-xs font-normal text-gray-400 dark:text-gray-500 mt-0.5 tabular-nums">
                          IMR: −{mechanicalOffsetForDisplay.toLocaleString()} {rawSuffix}
                        </span>
                      )}
                    </span>
                  ) : (
                    <Muted>{device.unitAssignedAt ? 'Awaiting first uplink' : 'No data yet'}</Muted>
                  )
                }
              />
            )}
            {isSalt && device.mechanicalOffset != null && (
              <Field
                label="Initial Meter Reading"
                value={<span className="tabular-nums">{device.mechanicalOffset.toLocaleString()}</span>}
                onClick={canEdit ? onEdit : undefined}
              />
            )}
            {isSalt && device.externalMeterModel && (
              <Field
                label="Connected Meter Model"
                value={device.externalMeterModel}
                onClick={canEdit ? onEdit : undefined}
              />
            )}
            {isSalt && device.externalMeterSerial && (
              <Field
                label="Connected Meter Serial"
                value={device.externalMeterSerial}
                mono
                onClick={canEdit ? onEdit : undefined}
              />
            )}
          </div>
          {device.notes && (
            canEdit ? (
              <button
                type="button"
                onClick={onEdit}
                title="Edit Notes"
                className="group mt-4 w-full text-left -mx-2 px-2 py-1 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40"
              >
                <p className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                  Notes
                  <Pencil className="w-3 h-3 text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity ml-auto" />
                </p>
                <p className="mt-1 text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                  {device.notes}
                </p>
              </button>
            ) : (
              <div className="mt-4">
                <p className="text-xs text-gray-500 dark:text-gray-400">Notes</p>
                <p className="mt-1 text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                  {device.notes}
                </p>
              </div>
            )
          )}
        </section>

        {/* ── Connection ──────────────────────────────────────────────
           Network identity + freshness. Kept separate per client
           feedback — "The separation of Connect info looks good". */}
        <section className="pt-5 border-t border-gray-200 dark:border-gray-700">
          <SectionLabel>Connection</SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
            <Field
              label="Connectivity"
              hint="Network state derived from the most recent uplink — Online (≤1h), Intermittent (≤24h), Offline (>24h), or Joined (radio-joined but no uplink yet)."
              value={<ConnectivityStatus lastSeenAt={device.lastSeenAt} joinedAt={device.joinedAt} />}
            />
            <Field label="Last Seen" value={device.lastSeenAt ? formatDateTime(device.lastSeenAt) : <Muted>Never</Muted>} />
            <Field label="DevEUI" value={device.devEui} mono />
            {gatewayEui && (
              <Field
                label="Gateway EUI"
                value={
                  matchedGateway ? (
                    <button
                      className="text-sm font-mono font-medium text-primary-600 dark:text-primary-400 hover:underline"
                      onClick={() => navigate(`/gateways/${matchedGateway.id}`)}
                    >
                      {gatewayEui}
                    </button>
                  ) : (
                    <span className="font-mono">{gatewayEui}</span>
                  )
                }
              />
            )}
            <Field
              label="Joined At"
              value={device.joinedAt ? formatDateTime(device.joinedAt) : <Muted>Not yet joined</Muted>}
            />
            <Field label="Created" value={formatDateTime(device.createdAt)} />
          </div>
        </section>
      </div>
    </Card>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
      {children}
    </h3>
  );
}

interface FieldProps {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  hint?: string;
  // When set, the whole field becomes a button. A pencil affordance
  // fades in on hover/focus to telegraph the interaction without adding
  // chrome to the resting state. `editTitle` overrides the default
  // "Edit <label>" tooltip — useful when the click does something other
  // than open the edit modal (e.g. "View alerts for this device").
  onClick?: () => void;
  editTitle?: string;
}

function Field({ label, value, mono, hint, onClick, editTitle }: FieldProps) {
  const labelRow = (
    <p className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
      {label}
      {hint && <HelpTip text={hint} />}
      {onClick && (
        <Pencil className="w-3 h-3 text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity ml-auto" />
      )}
    </p>
  );
  const valueRow = (
    <div className={`mt-0.5 text-sm font-medium text-gray-900 dark:text-gray-100 ${mono ? 'font-mono' : ''}`}>
      {value}
    </div>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={editTitle ?? `Edit ${label}`}
        className="group w-full text-left -mx-2 px-2 py-1 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40"
      >
        {labelRow}
        {valueRow}
      </button>
    );
  }
  return (
    <div>
      {labelRow}
      {valueRow}
    </div>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <span className="text-sm italic text-gray-400">{children}</span>;
}

// Hover/focus popover that replaces the native `title=` attribute. The
// browser-built-in tooltip has a ~500ms reveal delay and a tiny hover
// target, which made the Status/Connectivity hints feel broken. A real
// popover shows immediately, sits clearly anchored to the icon, and is
// pointer-events-none so it can't intercept clicks on the value below.
function HelpTip({ text }: { text: string }) {
  return (
    <span className="relative inline-flex items-center">
      <span
        tabIndex={0}
        role="button"
        aria-label={text}
        className="peer inline-flex items-center cursor-help rounded focus:outline-none focus-visible:ring-1 focus-visible:ring-primary-500"
      >
        <Info className="w-3 h-3 text-gray-400 dark:text-gray-500" />
      </span>
      <span
        role="tooltip"
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-1.5 z-20 w-64 px-2.5 py-1.5 text-[11px] leading-snug rounded-md bg-gray-900 dark:bg-gray-700 text-white shadow-lg opacity-0 invisible peer-hover:opacity-100 peer-hover:visible peer-focus-visible:opacity-100 peer-focus-visible:visible transition-opacity normal-case font-normal tracking-normal"
      >
        {text}
      </span>
    </span>
  );
}
